import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  TaxCategory,
  TaxCategoryPayload,
} from '../../services/tax-category.service';
import {
  fromExclusiveEndInstant,
  fromStartInstant,
} from '../../utils/tax-category-date.util';

export type TaxCategoryDialogMode = 'create' | 'edit' | 'view';

export interface TaxCategoryDialogResult {
  payload: TaxCategoryPayload;
}

interface TaxCategoryFormData {
  taxCode: string;
  taxName?: string | null;
  taxRate: number | null;
  activeFlag: boolean;
  effectiveFrom: Date | null;
  effectiveTo: Date | null;
  remark?: string | null;
  objectVersionNumber?: number | null;
}

@Component({
  selector: 'app-tax-category-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    AppButtonComponent,
    CheckboxModule,
    DatePickerModule,
    InputNumberModule,
    InputTextModule,
    TextareaModule,
  ],
  templateUrl: './tax-category-dialog.component.html',
  styleUrl: './tax-category-dialog.component.css',
})
export class TaxCategoryDialogComponent {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);

  showErrors = signal(false);

  readonly mode: TaxCategoryDialogMode = this.config.data?.mode ?? 'create';
  readonly isViewMode = this.mode === 'view';
  readonly isEditMode = this.mode === 'edit';

  formData: TaxCategoryFormData = {
    taxCode: '',
    taxName: '',
    taxRate: null,
    activeFlag: true,
    effectiveFrom: null,
    effectiveTo: null,
    remark: '',
    objectVersionNumber: null,
  };

  constructor() {
    const taxCategory: TaxCategory | undefined = this.config.data?.taxCategory;
    if (!taxCategory) {
      return;
    }

    this.formData = {
      taxCode: taxCategory.taxCode,
      taxName: taxCategory.taxName,
      taxRate: taxCategory.taxRate,
      activeFlag: taxCategory.activeFlag,
      effectiveFrom: fromStartInstant(taxCategory.effectiveFrom),
      effectiveTo: fromExclusiveEndInstant(taxCategory.effectiveTo),
      remark: taxCategory.remark,
      objectVersionNumber: taxCategory.objectVersionNumber,
    };
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    if (this.isViewMode) {
      this.dialogRef.close();
      return;
    }

    if (!this.isValid()) {
      this.showErrors.set(true);
      return;
    }

    this.dialogRef.close({
      payload: {
        taxCode: this.formData.taxCode.trim(),
        taxName: this.formData.taxName?.trim() || null,
        taxRate: this.formData.taxRate ?? 0,
        activeFlag: this.formData.activeFlag,
        effectiveFrom: new Date(this.formData.effectiveFrom!),
        effectiveTo: new Date(this.formData.effectiveTo!),
        remark: this.formData.remark?.trim() || null,
        objectVersionNumber: this.formData.objectVersionNumber,
      },
    } satisfies TaxCategoryDialogResult);
  }

  isInvalidDateRange(): boolean {
    if (!this.formData.effectiveFrom || !this.formData.effectiveTo) {
      return false;
    }
    return this.formData.effectiveFrom > this.formData.effectiveTo;
  }

  private isValid(): boolean {
    return (
      !!this.formData.taxCode.trim() &&
      this.formData.taxRate !== null &&
      this.formData.taxRate >= 0 &&
      !!this.formData.effectiveFrom &&
      !!this.formData.effectiveTo &&
      !this.isInvalidDateRange()
    );
  }

}
